import React, { useRef, useEffect, useCallback, useState } from 'react'
import { createPortal } from 'react-dom'
import { Link } from 'react-router-dom'
import type { NavCategory } from '../../types/nav.types'
import { useUIStore } from '../../store/uiStore'
import { useNavCategories } from '../../hooks/useNavCategories'

// ─── MegaMenu ─────────────────────────────────────────────────────────────────

export const MegaMenu: React.FC = () => {
  const { activeMegaMenu, openMegaMenu, closeMegaMenu } = useUIStore()
  const { categories, loading, error } = useNavCategories()
  const navRef      = useRef<HTMLDivElement>(null)
  const closeTimer  = useRef<ReturnType<typeof setTimeout> | null>(null)
  const [dropdownTop, setDropdownTop] = useState(0)

  const scheduleClose = useCallback(() => {
    closeTimer.current = setTimeout(closeMegaMenu, 150)
  }, [closeMegaMenu])

  const cancelClose = useCallback(() => {
    if (closeTimer.current) clearTimeout(closeTimer.current)
  }, [])

  useEffect(() => {
    if (activeMegaMenu && navRef.current)
      setDropdownTop(navRef.current.getBoundingClientRect().bottom)
  }, [activeMegaMenu])

  useEffect(() => {
    const update = () => {
      if (activeMegaMenu && navRef.current)
        setDropdownTop(navRef.current.getBoundingClientRect().bottom)
    }
    window.addEventListener('scroll', update, { passive: true })
    window.addEventListener('resize', update)
    return () => { window.removeEventListener('scroll', update); window.removeEventListener('resize', update) }
  }, [activeMegaMenu])

  useEffect(() => {
    const handler = (e: KeyboardEvent) => { if (e.key === 'Escape') closeMegaMenu() }
    document.addEventListener('keydown', handler)
    return () => document.removeEventListener('keydown', handler)
  }, [closeMegaMenu])

  const allCats: NavCategory[] = (categories as any)?.data ?? []
  const activeCategory = allCats.find(c => c.slug === activeMegaMenu)

  if (loading) {
    return (
      <div className="hidden lg:flex items-center h-11 overflow-x-auto" style={{ scrollbarWidth: 'none' }}>
        {Array.from({ length: 7 }).map((_, i) => (
          <div key={i} className="h-2.5 w-20 bg-white/10 rounded mx-3 shrink-0 animate-pulse" />
        ))}
      </div>
    )
  }

  if (error || allCats.length === 0) {
    return (
      <div className="hidden lg:flex items-center h-11">
        <NavTab href="/shop" label="All Products" />
        <NavTab href="/deals" label="Deals" />
        <NavTab href="/brands" label="Brands" />
      </div>
    )
  }

  return (
    <>
      {/* ── Tab strip on charcoal ── */}
      <div
        ref={navRef}
        className="hidden lg:flex items-center h-11"
        onMouseLeave={scheduleClose}
        onMouseEnter={cancelClose}
      >
        {/* Scrollable nav — handles overflow on smaller desktop viewports */}
        <nav
          aria-label="Main navigation"
          className="flex items-center flex-1 overflow-x-auto"
          style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
        >
          {allCats.map(cat => (
            <MegaMenuItem
              key={cat.id}
              category={cat}
              isActive={activeMegaMenu === cat.slug}
              onHover={() => { cancelClose(); openMegaMenu(cat.slug) }}
              onFocus={() => openMegaMenu(cat.slug)}
              onBlur={scheduleClose}
            />
          ))}
        </nav>

        {/* Right: Deals + Brands */}
        <div
          className="flex items-center shrink-0 pl-3 xl:pl-4 ml-2"
          style={{ borderLeft: '1px solid rgba(255,255,255,0.10)' }}
        >
          <NavTab href="/deals" label="Deals" highlight />
          <NavTab href="/brands" label="Brands" />
        </div>
      </div>

      {/* ── Portal dropdown ── */}
      {activeCategory && dropdownTop > 0 && createPortal(
        <MegaDropdown
          category={activeCategory}
          top={dropdownTop}
          onClose={closeMegaMenu}
          onMouseEnter={cancelClose}
          onMouseLeave={scheduleClose}
        />,
        document.body,
      )}
    </>
  )
}

// ─── MegaMenuItem ─────────────────────────────────────────────────────────────

interface MegaMenuItemProps {
  category: NavCategory
  isActive: boolean
  onHover: () => void
  onFocus: () => void
  onBlur: () => void
}

const MegaMenuItem: React.FC<MegaMenuItemProps> = ({ category, isActive, onHover, onFocus, onBlur }) => (
  <button
    type="button"
    aria-expanded={isActive}
    aria-haspopup="true"
    aria-controls={`mega-menu-${category.slug}`}
    onMouseEnter={onHover}
    onFocus={onFocus}
    onBlur={onBlur}
    className={`
      relative flex items-center gap-1 h-full px-3 xl:px-4 shrink-0
      font-display text-[11px] xl:text-xs font-semibold uppercase tracking-[0.07em] whitespace-nowrap
      transition-colors duration-150
      focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-royal-orange focus-visible:ring-inset
      ${isActive
        ? 'text-royal-orange'
        : 'text-white/60 hover:text-white'
      }
    `}
  >
    {isActive && (
      <span className="absolute bottom-0 left-0 right-0 h-0.5 bg-royal-orange" aria-hidden="true" />
    )}
    {category.name}
    <svg
      className={`w-3 h-3 transition-transform duration-150 shrink-0 ${isActive ? 'rotate-180 text-royal-orange' : 'text-white/30'}`}
      viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"
      aria-hidden="true"
    >
      <polyline points="6 9 12 15 18 9" />
    </svg>
  </button>
)

// ─── Plain nav tab ────────────────────────────────────────────────────────────

const NavTab: React.FC<{ href: string; label: string; highlight?: boolean }> = ({ href, label, highlight }) => (
  <Link
    to={href}
    className={`
      h-full flex items-center px-3 xl:px-4 shrink-0
      font-display text-[11px] xl:text-xs font-semibold uppercase tracking-[0.07em] whitespace-nowrap
      transition-colors duration-150
      focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-royal-orange focus-visible:ring-inset
      ${highlight ? 'text-royal-orange hover:text-royal-orange-light' : 'text-white/60 hover:text-white'}
    `}
  >
    {label}
  </Link>
)

// ─── MegaDropdown — portal-rendered ──────────────────────────────────────────

interface MegaDropdownProps {
  category: NavCategory
  top: number
  onClose: () => void
  onMouseEnter: () => void
  onMouseLeave: () => void
}

const MegaDropdown: React.FC<MegaDropdownProps> = ({ category, top, onClose, onMouseEnter, onMouseLeave }) => {
  const subcategories = category.children ?? []

  return (
    <div
      id={`mega-menu-${category.slug}`}
      role="region"
      aria-label={`${category.name} subcategories`}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
      style={{ position: 'fixed', top, left: 0, right: 0, zIndex: 9999 }}
      className="bg-white border-b border-border-color shadow-card-hover animate-fade-in border border-xls rounded-royal"
    >
      {/* Orange accent line */}
      <div className="h-0.5 bg-royal-orange" aria-hidden="true" />

      <div className="max-w-screen-2xl mx-auto px-4 md:px-8 py-4 md:py-6">
        <div className="flex gap-4 md:gap-8">

          {/* ── Category identity column ── */}
          {/* Hidden on medium viewport when space is tight; shown from lg where MegaMenu lives */}
          <div className="hidden xl:flex w-40 2xl:w-48 shrink-0 flex-col gap-4">
            <Link
              to={`/shop/${category.slug}`}
              onClick={onClose}
              className="group focus-visible:outline-none"
            >
              {category.image && (
                <div className="w-full aspect-[4/3] rounded-royal overflow-hidden mb-3 border border-border-color bg-royal-ivory">
                  <img
                    src={category.image}
                    alt=""
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                    loading="lazy"
                  />
                </div>
              )}

              <p className="font-display font-bold text-sm text-charcoal group-hover:text-royal-orange transition-colors duration-150 leading-snug">
                {category.name}
              </p>
              {category.product_count != null && (
                <p className="font-sans text-xs text-charcoal-light mt-0.5 tabular-nums">
                  {category.product_count.toLocaleString()} products
                </p>
              )}

              <span className="mt-2.5 inline-flex items-center gap-1.5 font-display text-xs font-bold uppercase tracking-[0.08em] text-royal-orange">
                Shop all
                <svg className="w-3 h-3 transition-transform duration-150 group-hover:translate-x-0.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" aria-hidden="true">
                  <line x1="5" y1="12" x2="19" y2="12" /><polyline points="12 5 19 12 12 19" />
                </svg>
              </span>
            </Link>
          </div>

          {/* Vertical rule — only shown when identity column is visible */}
          <div className="hidden xl:block w-px bg-border-color shrink-0" aria-hidden="true" />

          {/* ── Subcategory grid ── */}
          {subcategories.length > 0 ? (
            <div className="flex-1 min-w-0">
              {/* Eyebrow */}
              <div className="flex items-center gap-2 mb-3">
                <span className="block w-1 h-3.5 rounded-full bg-royal-orange" aria-hidden="true" />
                <p className="font-display text-[10px] font-bold uppercase tracking-[0.18em] text-charcoal-light">
                  Browse {category.name}
                </p>

                {/* "Shop all" link inline — shown when identity column is hidden */}
                <Link
                  to={`/shop/${category.slug}`}
                  onClick={onClose}
                  className="xl:hidden ml-auto inline-flex items-center gap-1 font-display text-[10px] font-bold uppercase tracking-[0.08em] text-royal-orange hover:text-royal-orange-dark transition-colors focus-visible:outline-none focus-visible:underline"
                >
                  Shop all
                  <svg className="w-2.5 h-2.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" aria-hidden="true">
                    <line x1="5" y1="12" x2="19" y2="12" /><polyline points="12 5 19 12 12 19" />
                  </svg>
                </Link>
              </div>

              {/* Responsive grid: 2 cols on lg, 3 on xl, 4 on 2xl */}
              <ul
                className="grid grid-cols-2 xl:grid-cols-3 2xl:grid-cols-4 gap-x-4 md:gap-x-6 gap-y-0"
                role="list"
                aria-label={`${category.name} subcategories`}
              >
                {subcategories.map(sub => (
                  <li key={sub.id}>
                    <Link
                      to={`/shop/${category.slug}/${sub.slug}`}
                      onClick={onClose}
                      className="group flex items-center gap-2 py-2 md:py-2.5 border-b border-border-color/50 focus-visible:outline-none"
                    >
                      <span
                        className="block w-0 h-px bg-royal-orange transition-all duration-150 group-hover:w-3 shrink-0"
                        aria-hidden="true"
                      />
                      <span className="font-sans text-sm text-charcoal group-hover:text-royal-orange transition-colors duration-150 truncate">
                        {sub.name}
                      </span>
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
          ) : (
            <div className="flex-1 flex items-center">
              <Link
                to={`/shop/${category.slug}`}
                onClick={onClose}
                className="inline-flex items-center gap-2 font-display text-sm font-bold text-royal-orange hover:text-royal-orange-dark transition-colors duration-150 focus-visible:outline-none focus-visible:underline"
              >
                Browse all {category.name}
                <svg className="w-3.5 h-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" aria-hidden="true">
                  <line x1="5" y1="12" x2="19" y2="12" /><polyline points="12 5 19 12 12 19" />
                </svg>
              </Link>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}